<template>
    <exportable-table v-model="groupsList" rowKey="group_id" :headers="headers" :loading="loading" :filename="filename">
        <template v-slot:tableTitle="{ totalCount }">{{ pluralized('groups', totalCount, true) }}</template>
        <template v-slot:rowTitle="{ item }">
            <div class="flex">
                <vx-badge class="mr-2 cursor-pointer" @click.prevent="copyText(item.group_id)" size="xs" color="slate">
                    <copy-button label="group ID" :value="item.group_id" />
                </vx-badge>
                <div class="line-clamp-none break-all line-clamp-2">
                    {{ item.group_name || '-' }}
                </div>
            </div>
        </template>
        <template v-slot:cell="{ value, field }">
            <template v-if="field === 'total_messages_sent'">
                <vx-popover
                    v-if="formatCount(value) !== ''"
                    arrow
                    hover
                    placement="top"
                    class="cursor-pointer"
                    @click="copyText(value)"
                >
                    <copy-button :label="metricLabel(field)" :value="formatNumber(value)">
                        {{ formatCount(value) }}
                    </copy-button>
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(value)">
                            {{ formatNumber(value) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else-if="metricsWithGoals.includes(field)">
                <vx-popover
                    v-if="value !== null"
                    arrow
                    hover
                    placement="top"
                    @click="copyText(formatFractionToPercent(value, true))"
                    class="cursor-pointer"
                >
                    <copy-button :label="metricLabel(field)" :value="formatFractionToPercent(value, true)">
                        {{ formatPercentage(value, true) }}
                    </copy-button>
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(formatFractionToPercent(value, true))">
                            {{ formatFractionToPercent(value, true) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else>
                {{ value }}
            </template>
        </template>
        <template v-slot:buttons="{ item }">
            <exportable-table-row-buttons>
                <vx-button
                    :href="`/analytics/franchise-hub/${props.category}/${props.metric}/groups/${
                        item.group_id
                    }?${stringifyQueryString(fetchParams)}`"
                    color="muted"
                    outline
                    size="sm"
                    class="grow sm:grow-0"
                >
                    View <font-awesome-icon widthAuto :icon="faEye" />
                </vx-button>
            </exportable-table-row-buttons>
        </template>
    </exportable-table>
</template>

<script setup>
import ExportableTable from '~/components/analytics/partial/exportable-table/ExportableTable.vue';
import { ref, watch } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { useListAllPaginated } from '~/composables/useListAllPaginated';
import {
    metricAbbreviation,
    metricLabel,
    metricsInputValidator,
    metricsWithGoals,
    isReversedMetric,
    addCategoryToFetchParams,
} from '~/utils/analytics';
import { analyticsCategoriesValues } from '~/constants/categories';
import { formatPercentage, pluralized, formatCount } from '~/components/filters';
import { VxBadge, VxButton, VxPopover } from '@voxie/frontend-components';
import { useCopyText } from '~/composables/useCopyText';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import ExportableTableRowButtons from '../../partial/exportable-table/ExportableTableRowButtons.vue';
import CopyButton from '~/components/general/CopyButton.vue';
import { stringifyQueryString } from '~/utils/string';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatFractionToPercent, formatNumber } from '~/utils/helpers';

const teamId = Spark.state.currentTeam.id;
const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    filename: {
        type: String,
        required: true,
    },
    groupIds: {
        type: Array,
        required: false,
    },
    target: {
        type: Number,
        default: null,
    },
    aboveTarget: {
        type: Boolean,
        default: null,
    },
});

const { copyText } = useCopyText();

const groupsList = ref([]);
const fetchAllParams = ref();
const { list, fetchAll, loading } = useListAllPaginated(fetchAllParams);

const headers = [
    { field: 'group_id', hidden: true },
    { field: 'group_name', label: 'Name', title: true, sortable: true },
    {
        field: 'total_messages_sent',
        label: metricAbbreviation('total_messages_sent'),
        popoverContent: metricLabel('total_messages_sent'),
        sortable: true,
    },
    {
        field: 'unique_click_through_rate',
        label: metricAbbreviation('unique_click_through_rate'),
        popoverContent: metricLabel('unique_click_through_rate'),
        sortable: true,
    },
    {
        field: 'click_through_rate',
        label: metricAbbreviation('click_through_rate'),
        popoverContent: metricLabel('click_through_rate'),
        sortable: true,
    },
    {
        field: 'opt_out_rate',
        label: metricAbbreviation('opt_out_rate'),
        popoverContent: metricLabel('opt_out_rate'),
        sortable: true,
    },
    {
        field: 'message_reply_rate',
        label: metricAbbreviation('message_reply_rate'),
        popoverContent: metricLabel('message_reply_rate'),
        sortable: true,
    },
];

watch(
    () => props.fetchParams,
    () => {
        fetchAllParams.value = JSON.parse(JSON.stringify(props.fetchParams));
        const fetchParams = addCategoryToFetchParams(fetchAllParams.value, props.category);
        fetchAll(() => outboundMessagesAnalyticsClient.getExportGroupMetrics({ teamId }, fetchParams));
    },
    {
        deep: true,
        immediate: true,
    }
);

watch(loading, (newValue) => {
    if (newValue) {
        return;
    }

    if (props.groupIds?.length) {
        groupsList.value = list.value.filter((group) => props.groupIds.includes(group.group_id));
    } else if (props.target !== null && props.aboveTarget !== null) {
        groupsList.value = list.value.filter(
            (group) =>
                group[props.metric] !== null &&
                (props.aboveTarget === !isReversedMetric(props.metric)
                    ? group[props.metric] >= props.target
                    : group[props.metric] < props.target)
        );
    } else {
        groupsList.value = list.value.map((group) => ({ ...group }));
    }
});
</script>
